import React from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Warehouse,
  ShoppingCart,
  MoreHorizontal,
} from 'lucide-react';
import { useMobile } from '../../../hooks/useMobile';
import { useMobileNavigation } from '../../../contexts/MobileNavigationContext';
import { mobileTokens } from '../../../theme/mobileTokens';

/**
 * Navigation Item Configuration
 */
interface NavItem {
  label: string;
  path: string | null;
  icon: React.ElementType;
  ariaLabel: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Tableau',
    path: '/app/dashboard',
    icon: LayoutDashboard,
    ariaLabel: 'Tableau de bord',
  },
  {
    label: 'Produits',
    path: '/app/products',
    icon: Package,
    ariaLabel: 'Produits',
  },
  {
    label: 'Inventaire',
    path: '/app/inventory',
    icon: Warehouse,
    ariaLabel: 'Inventaire',
  },
  {
    label: 'Ventes',
    path: '/app/sales',
    icon: ShoppingCart,
    ariaLabel: 'Ventes',
  },
  {
    label: 'Plus',
    path: null,
    icon: MoreHorizontal,
    ariaLabel: 'Plus',
  },
];

/**
 * MobileBottomNavigation Component
 *
 * Material Design compliant bottom navigation for mobile devices.
 * Displays 5 primary navigation items with icons and labels.
 *
 * Features:
 * - Fixed to bottom of viewport
 * - Material Design touch targets (48x48px minimum)
 * - Active route highlighting
 * - Responsive (mobile only <768px)
 * - iOS safe area support
 * - Full accessibility (ARIA labels, keyboard nav)
 *
 * @example
 * ```tsx
 * <MobileBottomNavigation />
 * ```
 */
export const MobileBottomNavigation: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMobile();
  const { toggleDrawer } = useMobileNavigation();

  // Don't render on desktop/tablet
  if (!isMobile) {
    return null;
  }

  // Determine active navigation item based on current route
  const activeValue = NAV_ITEMS.findIndex(
    (item) => item.path && location.pathname.startsWith(item.path)
  );

  /**
   * Handle navigation item click
   */
  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    const item = NAV_ITEMS[newValue];

    // If "More" button, toggle drawer instead of navigating
    if (item.path === null) {
      toggleDrawer();
      return;
    }

    // Navigate to selected route
    navigate(item.path);
  };

  return (
    <Paper
      component="nav"
      aria-label="Navigation principale"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: mobileTokens.zIndex.fixed,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
      elevation={8}
    >
      <BottomNavigation
        value={activeValue}
        onChange={handleChange}
        showLabels
        sx={{
          height: `${mobileTokens.bottomNav.height}px`,
          '& .MuiBottomNavigationAction-root': {
            minHeight: `${mobileTokens.touchTargets.min}px`,
            minWidth: `${mobileTokens.bottomNav.itemMinWidth}px`,
            maxWidth: `${mobileTokens.bottomNav.itemMaxWidth}px`,
            padding: theme.spacing(0.5),
            color: theme.palette.text.secondary,
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: `${mobileTokens.bottomNav.labelSize}px`,
              fontWeight: 500,
              marginTop: theme.spacing(0.5),
              '&.Mui-selected': {
                fontSize: `${mobileTokens.bottomNav.labelSize}px`,
              },
            },
          },
          '& svg': {
            fontSize: `${mobileTokens.bottomNav.iconSize}px`,
            width: `${mobileTokens.bottomNav.iconSize}px`,
            height: `${mobileTokens.bottomNav.iconSize}px`,
          },
        }}
      >
        {NAV_ITEMS.map((item) => {
          const IconComponent = item.icon;
          return (
            <BottomNavigationAction
              key={item.label}
              label={item.label}
              aria-label={item.ariaLabel}
              icon={<IconComponent />}
            />
          );
        })}
      </BottomNavigation>
    </Paper>
  );
};
